// @ts-check
// Seletor de views (doc 06, seção 3): views da equipe e pessoais, opções por view (favoritar, renomear, duplicar,
// tornar pessoal ou compartilhar, excluir) e criação de view (tabela, kanban ou calendário) a partir da atual.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { currentWorkspaceMemberState, favoritesState, getViewsForObject, replaceViewLocally, sessionState } from '../../data/metadata-store.js';
import { updateView } from '../../data/rest.js';
import { addFavorite, buildViewCopy, createView, deleteView, removeFavorite } from '../../data/parity.js';
import { FIELD_TYPE, isManyToOneRelation } from '../../lib/field-metadata.js';
import { formatNumber } from '../../lib/format.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { buildViewShareUrl, getViewDraftPayload } from './view-state.js';
import { buildViewSwitchSearch } from './view-url-params.js';
import { computeViewMoveChanges, computeViewPositionChanges } from './view-order.js';
import { IconPicker } from '../settings/data-model/pickers.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { useListCommandRequest } from '../command-menu/command-state.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 */

export const VIEW_TYPE_ICONS = /** @type {Record<string, string>} */ ({ TABLE: 'IconTable', KANBAN: 'IconLayoutKanban', CALENDAR: 'IconCalendar' });
const CREATABLE_VIEW_TYPES = ['TABLE', 'KANBAN', 'CALENDAR'];

/** Campos que agrupam um kanban: SELECT ou relação "muitos para um" (doc 06, 3.4). @param {ObjectMetadata} objectMetadata */
export const getGroupableFields = (objectMetadata) => objectMetadata.fields.filter((field) => field.isActive !== false
  && (field.type === FIELD_TYPE.SELECT || (isManyToOneRelation(field) && !field.isSystem)));

/** Campos de data que posicionam o registro no calendário. @param {ObjectMetadata} objectMetadata */
export const getCalendarFields = (objectMetadata) => objectMetadata.fields.filter((field) => field.isActive !== false
  && (field.type === FIELD_TYPE.DATE || field.type === FIELD_TYPE.DATE_TIME) && field.name !== 'deletedAt');

/** @param {ObjectMetadata} objectMetadata @param {string} viewId */
const viewHref = (objectMetadata, viewId) => `/objects/${objectMetadata.namePlural}?viewId=${viewId}`;

/**
 * Pode mudar a visibilidade: quem criou a view ou o administrador; a view principal é sempre de todos.
 * @param {View} view
 */
function canChangeVisibility(view) {
  if (view.key === 'INDEX') return false;
  const isOwner = Boolean(view.createdByWorkspaceMemberId) && view.createdByWorkspaceMemberId === currentWorkspaceMemberState.value?.id;
  return isOwner || sessionState.value?.role === 'ADMIN';
}

/**
 * Formulário de criação: nome, tipo, campo do kanban ou do calendário, visibilidade e cópia dos filtros atuais.
 * @param {{ objectMetadata: ObjectMetadata, view: View, onDone: () => void, onCancel: () => void, initialType?: string }} props
 *   initialType: "Criar view kanban" do menu de comandos já chega com o tipo escolhido
 */
function CreateViewForm({ objectMetadata, view, onDone, onCancel, initialType = 'TABLE' }) {
  const groupableFields = getGroupableFields(objectMetadata);
  const calendarFields = getCalendarFields(objectMetadata);
  const [name, setName] = useState('');
  const [type, setType] = useState(CREATABLE_VIEW_TYPES.includes(initialType) ? initialType : 'TABLE');
  // ícone segue o tipo até a pessoa escolher outro, como no Twenty (doc 06, seção 3.4)
  const [chosenIcon, setChosenIcon] = useState(/** @type {string | null} */ (null));
  const icon = chosenIcon ?? VIEW_TYPE_ICONS[type];
  const [groupFieldId, setGroupFieldId] = useState(view.mainGroupByFieldMetadataId ?? groupableFields[0]?.id ?? '');
  const [calendarFieldId, setCalendarFieldId] = useState(view.calendarFieldMetadataId ?? calendarFields[0]?.id ?? '');
  const [isPersonal, setIsPersonal] = useState(false);
  const [copyFilters, setCopyFilters] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isMissingField = (type === 'KANBAN' && !groupFieldId) || (type === 'CALENDAR' && !calendarFieldId);

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!name.trim() || isMissingField) return;
    setIsSaving(true);
    try {
      const draft = getViewDraftPayload(view);
      const created = await createView(buildViewCopy(view, {
        name: name.trim(),
        type,
        icon,
        visibility: isPersonal ? 'UNLISTED' : 'WORKSPACE',
        mainGroupByFieldMetadataId: type === 'KANBAN' ? groupFieldId : view.type === 'KANBAN' && type !== 'KANBAN' ? null : undefined,
        calendarFieldMetadataId: type === 'CALENDAR' ? calendarFieldId : undefined,
        ...(copyFilters ? draft : { viewFilters: [], viewFilterGroups: [], viewSorts: [] }),
      }));
      showSuccessSnackbar(t('parity.views.created'));
      onDone();
      navigate(viewHref(objectMetadata, created.id));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  return html`<form class="view-create-form" onSubmit=${submit}>
    <${DropdownMenuHeader} title=${t('parity.views.createTitle')} onClose=${onCancel} />
    <div class="view-create-body">
      <div class="view-name-row">
        <${IconPicker} value=${icon} onChange=${setChosenIcon} ariaLabel=${t('viewsFilters.views.icon')} />
        <input class="text-input" placeholder=${t('parity.views.namePlaceholder')} value=${name} aria-label=${t('parity.views.name')}
          ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.focus()))}
          onInput=${(event) => setName(event.currentTarget.value)} />
      </div>
      <label class="view-create-label">${t('parity.views.type')}
        <select class="text-input" value=${type} onChange=${(event) => setType(event.currentTarget.value)}>
          ${CREATABLE_VIEW_TYPES.map((candidate) => html`<option value=${candidate}>${t(`viewTypes.${candidate}`)}</option>`)}
        </select>
      </label>
      ${type === 'KANBAN'
        ? groupableFields.length > 0
          ? html`<label class="view-create-label">${t('parity.views.groupBy')}
              <select class="text-input" value=${groupFieldId} onChange=${(event) => setGroupFieldId(event.currentTarget.value)}>
                ${groupableFields.map((field) => html`<option value=${field.id}>${translateMetadataLabel(field.label)}</option>`)}
              </select>
            </label>`
          : html`<p class="view-create-hint">${t('parity.views.noGroupField', { object: translateMetadataLabel(objectMetadata.labelSingular) })}</p>`
        : null}
      ${type === 'CALENDAR'
        ? calendarFields.length > 0
          ? html`<label class="view-create-label">${t('parity.views.dateField')}
              <select class="text-input" value=${calendarFieldId} onChange=${(event) => setCalendarFieldId(event.currentTarget.value)}>
                ${calendarFields.map((field) => html`<option value=${field.id}>${translateMetadataLabel(field.label)}</option>`)}
              </select>
            </label>`
          : html`<p class="view-create-hint">${t('parity.views.noDateField', { object: translateMetadataLabel(objectMetadata.labelSingular) })}</p>`
        : null}
      <label class="view-create-check"><input type="checkbox" checked=${copyFilters} onChange=${(event) => setCopyFilters(event.currentTarget.checked)} />
        <span>${t('parity.views.copyFilters')}</span></label>
      <label class="view-create-check"><input type="checkbox" checked=${isPersonal} onChange=${(event) => setIsPersonal(event.currentTarget.checked)} />
        <span>${t('parity.views.personal')}</span></label>
    </div>
    <div class="view-create-footer">
      <${Button} title=${t('parity.views.create')} size="small" accent="blue" type="submit" disabled=${!name.trim() || isMissingField} isLoading=${isSaving} />
    </div>
  </form>`;
}

/**
 * @param {{ view: View, onDone: () => void, onCancel: () => void }} props
 */
function RenameViewForm({ view, onDone, onCancel }) {
  const [name, setName] = useState(translateMetadataLabel(view.name));
  const [icon, setIcon] = useState(view.icon ?? VIEW_TYPE_ICONS[view.type] ?? 'IconList');
  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      const updated = await updateView(view.id, { name: name.trim(), icon });
      replaceViewLocally({ ...view, ...(updated && typeof updated === 'object' ? updated : {}), name: name.trim(), icon });
      onDone();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  return html`<form class="view-create-form" onSubmit=${submit}>
    <${DropdownMenuHeader} title=${t('viewsFilters.views.editTitle')} onBack=${onCancel} />
    <div class="view-create-body">
      <div class="view-name-row">
        <${IconPicker} value=${icon} onChange=${setIcon} ariaLabel=${t('viewsFilters.views.icon')} />
        <input class="text-input" value=${name} aria-label=${t('parity.views.name')}
          ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.select()))}
          onInput=${(event) => setName(event.currentTarget.value)} />
      </div>
    </div>
    <div class="view-create-footer"><${Button} title=${t('common.save')} size="small" accent="blue" type="submit" disabled=${!name.trim()} /></div>
  </form>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, totalCount: number | null, isDirty: boolean }} props
 */
export function ViewPicker({ objectMetadata, view, totalCount, isDirty }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(/** @type {{ kind: 'list' } | { kind: 'create', viewType?: string } | { kind: 'options', viewId: string } | { kind: 'rename', viewId: string }} */ ({ kind: 'list' }));
  useListCommandRequest('createView', view.id, (request) => {
    setMode({ kind: 'create', viewType: request.viewType });
    setIsOpen(true);
  });
  const views = getViewsForObject(objectMetadata.id);
  const workspaceViews = views.filter((candidate) => candidate.visibility !== 'UNLISTED');
  const personalViews = views.filter((candidate) => candidate.visibility === 'UNLISTED');
  const favoriteByViewId = new Map(favoritesState.value.filter((favorite) => favorite.viewId).map((favorite) => [favorite.viewId, favorite]));
  const close = () => {
    setIsOpen(false);
    setMode({ kind: 'list' });
  };
  const optionsView = mode.kind === 'options' || mode.kind === 'rename' ? views.find((candidate) => candidate.id === mode.viewId) : null;
  const [draggedViewId, setDraggedViewId] = useState(/** @type {string | null} */ (null));
  const [dropTargetId, setDropTargetId] = useState(/** @type {string | null} */ (null));

  /** Grava as posições novas; a tela muda na hora e volta atrás se a API recusar. @param {Array<{ id: string, position: number }>} changes */
  const persistPositions = async (changes) => {
    const previous = changes.map((change) => views.find((candidate) => candidate.id === change.id)).filter(Boolean);
    for (const change of changes) {
      const current = views.find((candidate) => candidate.id === change.id);
      if (current) replaceViewLocally({ ...current, position: change.position });
    }
    try {
      for (const change of changes) await updateView(change.id, { position: change.position });
    } catch (error) {
      for (const original of previous) replaceViewLocally(/** @type {View} */ (original));
      showErrorSnackbar(error);
    }
  };

  const run = async (/** @type {() => Promise<void>} */ action) => {
    try {
      await action();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  /** Arrastar dentro da seção reordena (views da equipe e pessoais ficam cada uma na sua). @param {View} candidate @param {View[]} section */
  const renderItem = (candidate, section) => html`<div
    key=${candidate.id}
    class="view-picker-item"
    draggable=${section.length > 1 ? 'true' : 'false'}
    data-drop-target=${dropTargetId === candidate.id && draggedViewId !== candidate.id ? 'true' : undefined}
    data-view-id=${candidate.id}
    onDragStart=${(/** @type {DragEvent} */ event) => {
      setDraggedViewId(candidate.id);
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', candidate.id);
      }
    }}
    onDragOver=${(/** @type {DragEvent} */ event) => {
      if (!draggedViewId || !section.some((item) => item.id === draggedViewId)) return;
      event.preventDefault();
      setDropTargetId(candidate.id);
    }}
    onDragEnd=${() => {
      setDraggedViewId(null);
      setDropTargetId(null);
    }}
    onDrop=${(/** @type {DragEvent} */ event) => {
      event.preventDefault();
      const changes = draggedViewId ? computeViewPositionChanges(section, draggedViewId, candidate.id) : [];
      setDraggedViewId(null);
      setDropTargetId(null);
      if (changes.length > 0) persistPositions(changes);
    }}
  ><${MenuItem}
    icon=${candidate.icon ?? VIEW_TYPE_ICONS[candidate.type] ?? 'IconList'}
    text=${translateMetadataLabel(candidate.name)}
    contextualText=${t(`viewTypes.${candidate.type}`)}
    selected=${candidate.id === view.id}
    rightComponent=${html`
      ${favoriteByViewId.has(candidate.id) ? html`<${Icon} name="IconHeart" size=${14} className="menu-item-muted" />` : null}
      ${candidate.key === 'INDEX' ? html`<${Icon} name="IconLock" size=${14} className="menu-item-muted" />` : null}
      <span class="view-picker-options" onClick=${(/** @type {MouseEvent} */ event) => {
        event.stopPropagation();
        setMode({ kind: 'options', viewId: candidate.id });
      }}><${LightIconButton} icon="IconDots" ariaLabel=${t('parity.views.options')} /></span>`}
    onClick=${() => {
      close();
      // na lista de um clique num gráfico, o recorte do painel segue para a outra view (tabela, kanban, calendário)
      navigate(`/objects/${objectMetadata.namePlural}${buildViewSwitchSearch(window.location.search, candidate.id)}`);
    }}
  /></div>`;

  let content;
  if (mode.kind === 'create') {
    content = html`<${CreateViewForm} key=${mode.viewType ?? 'TABLE'} objectMetadata=${objectMetadata} view=${view} initialType=${mode.viewType} onDone=${close} onCancel=${() => setMode({ kind: 'list' })} />`;
  } else if (mode.kind === 'rename' && optionsView) {
    content = html`<${RenameViewForm} view=${optionsView} onDone=${() => setMode({ kind: 'list' })} onCancel=${() => setMode({ kind: 'options', viewId: optionsView.id })} />`;
  } else if (mode.kind === 'options' && optionsView) {
    const favorite = favoriteByViewId.get(optionsView.id);
    const isPersonal = optionsView.visibility === 'UNLISTED';
    const isOnlyView = views.length <= 1;
    const section = views.filter((candidate) => (candidate.visibility === 'UNLISTED') === isPersonal);
    const sectionIndex = section.findIndex((candidate) => candidate.id === optionsView.id);
    content = html`
      <${DropdownMenuHeader} title=${translateMetadataLabel(optionsView.name)} onBack=${() => setMode({ kind: 'list' })} />
      <${DropdownMenuItems}>
        <${MenuItem} icon=${favorite ? 'IconHeartOff' : 'IconHeart'} text=${favorite ? t('parity.favorites.remove') : t('parity.favorites.add')} onClick=${() => run(async () => {
          if (favorite) await removeFavorite(favorite.id);
          else await addFavorite({ type: 'VIEW', viewId: optionsView.id });
        })} />
        ${optionsView.key !== 'INDEX' ? html`<${MenuItem} icon="IconPencil" text=${t('viewsFilters.views.edit')} onClick=${() => setMode({ kind: 'rename', viewId: optionsView.id })} />` : null}
        <${MenuItem} icon="IconLink" text=${t('viewsFilters.views.copyLink')} onClick=${() => run(async () => {
          await copyViewLink(optionsView.id === view.id ? buildViewShareUrl(view, objectMetadata.namePlural) : `${window.location.origin}${viewHref(objectMetadata, optionsView.id)}`);
          close();
        })} />
        ${section.length > 1
          ? html`<${MenuItem} icon="IconArrowUp" text=${t('viewsFilters.views.moveUp')} disabled=${sectionIndex <= 0}
              onClick=${() => persistPositions(computeViewMoveChanges(section, optionsView.id, -1))} />
            <${MenuItem} icon="IconArrowDown" text=${t('viewsFilters.views.moveDown')} disabled=${sectionIndex >= section.length - 1}
              onClick=${() => persistPositions(computeViewMoveChanges(section, optionsView.id, 1))} />`
          : null}
        <${MenuItem} icon="IconCopy" text=${t('parity.views.duplicate')} onClick=${() => run(async () => {
          const copy = await createView(buildViewCopy(optionsView, {
            name: t('parity.views.copyName', { name: translateMetadataLabel(optionsView.name) }),
            visibility: optionsView.key === 'INDEX' ? 'WORKSPACE' : optionsView.visibility,
          }));
          close();
          navigate(viewHref(objectMetadata, copy.id));
        })} />
        ${canChangeVisibility(optionsView)
          ? html`<${MenuItem} icon=${isPersonal ? 'IconUsers' : 'IconLock'} text=${isPersonal ? t('parity.views.share') : t('parity.views.makePersonal')} onClick=${() => run(async () => {
              const visibility = isPersonal ? 'WORKSPACE' : 'UNLISTED';
              const updated = await updateView(optionsView.id, { visibility });
              replaceViewLocally({ ...optionsView, ...(updated && typeof updated === 'object' ? updated : {}), visibility });
              setMode({ kind: 'list' });
            })} />`
          : null}
        ${optionsView.key !== 'INDEX' && !isOnlyView
          ? html`<${DropdownMenuSeparator} />
            <${MenuItem} icon="IconTrash" accent="danger" text=${t('parity.views.delete')} onClick=${() => run(async () => {
              await deleteView(optionsView.id);
              showSuccessSnackbar(t('parity.views.deleted'));
              setMode({ kind: 'list' });
              if (optionsView.id === view.id) navigate(`/objects/${objectMetadata.namePlural}`);
            })} />`
          : null}
      <//>`;
  } else {
    content = html`
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${personalViews.length > 0 ? html`<${DropdownMenuSectionLabel} label=${t('parity.views.workspaceSection')} />` : null}
        ${workspaceViews.map((candidate) => renderItem(candidate, workspaceViews))}
        ${personalViews.length > 0 ? html`<${DropdownMenuSectionLabel} label=${t('parity.views.personalSection')} />` : null}
        ${personalViews.map((candidate) => renderItem(candidate, personalViews))}
      <//>
      <${DropdownMenuSeparator} />
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconPlus" text=${t('parity.views.add')} onClick=${() => setMode({ kind: 'create' })} />
      <//>`;
  }

  return html`<div class="view-picker">
    <button ref=${anchorRef} type="button" class="view-picker-button" onClick=${() => (isOpen ? close() : setIsOpen(true))} aria-haspopup="listbox" aria-expanded=${isOpen}>
      <${Icon} name=${view.icon ?? VIEW_TYPE_ICONS[view.type] ?? 'IconList'} size=${16} />
      <span class="view-picker-name">${translateMetadataLabel(view.name)}</span>
      ${view.visibility === 'UNLISTED' ? html`<${Icon} name="IconLock" size=${12} className="view-picker-personal" title=${t('parity.views.personalHint')} />` : null}
      ${totalCount !== null ? html`<span class="view-picker-count">· ${formatNumber(totalCount)}</span>` : null}
      ${isDirty ? html`<span class="view-picker-dirty" title=${t('viewBar.unsavedChanges')}></span>` : null}
      <${Icon} name="IconChevronDown" size=${14} className="view-picker-chevron" />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} width=${280}>${content}<//>
  </div>`;
}

/**
 * "Salvar como nova view" da linha de detalhes: a view atual com os filtros e ordenações em rascunho.
 * @param {{ objectMetadata: ObjectMetadata, view: View, onSaved: () => void }} props
 */
export function SaveAsNewViewButton({ objectMetadata, view, onSaved }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  return html`<span>
    <button ref=${anchorRef} type="button" class="light-button" data-testid="save-as-new-view" onClick=${() => setIsOpen(!isOpen)}>${t('parity.views.saveAsNew')}</button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${280} placement="bottom-end">
      <${CreateViewForm} objectMetadata=${objectMetadata} view=${view} onDone=${() => {
        setIsOpen(false);
        onSaved();
      }} onCancel=${() => setIsOpen(false)} />
    <//>
  </span>`;
}


/**
 * Copia o link da view (com filtros e ordenações do rascunho) e avisa, como "Copy link to view" do Twenty.
 * @param {string} url
 */
export async function copyViewLink(url) {
  await navigator.clipboard.writeText(url);
  showSuccessSnackbar(t('viewsFilters.views.linkCopied'));
}
